<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import DispatcherInformation from "@/views/dispatchers/components/DispatcherInformation.vue";
import { useRouter } from "vue-router";
import DispatcherPermissions from "@/views/dispatchers/components/DispatcherPermissions.vue";
import type { DispatcherDto } from "@/api/dispatchers/dto/dispatcher.dto";
import { API } from "@/api/api";
import type { VForm } from "vuetify/components";
import { useError } from "@/composables/useError";
import { useSnackbarStore } from "@/store/snackbar.store";
import DispatcherRemoveDialog from "@/views/dispatchers/components/DispatcherRemoveDialog.vue";
import DispatcherArchiveDialog from "@/views/dispatchers/components/DispatcherArchiveDialog.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";

const { t } = useI18n();
const { showAPIError, scrollToError } = useError();
const { showSnackbarSuccess } = useSnackbarStore();
const router = useRouter();
const formRef = ref<VForm | null>(null);
const api = new API();

const props = withDefaults(
  defineProps<{
    existedDispatcher?: DispatcherDto | null;
  }>(),
  {
    existedDispatcher: null,
  },
);

const saving = ref<boolean>(false);
const showRemoveDialog = ref<boolean>(false);
const showArchiveDialog = ref<boolean>(false);
const showUnArchiveDialog = ref<boolean>(false);

const isArchive = computed(() => {
  return props.existedDispatcher?.archived;
});

const isDelete = computed(() => {
  return props.existedDispatcher?.id;
});

const dispatcher = ref<DispatcherDto>({
  roles: [],
  email: null,
  emailConfirmed: false,
  name: null,
  phone: null,
  phoneExt: null,
  permissions: [],
  firstName: "",
  lastName: "",
});

async function goToDispatchers() {
  await router.push({ name: "dispatchers" });
}

async function save() {
  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError();
    return;
  }

  saving.value = true;

  try {
    const response = await api.dispatchers.upsertDispatcher(dispatcher.value);

    if (response.errorData) {
      showAPIError(response);
      return;
    }

    if (response.success) {
      showSnackbarSuccess(t("general.saved"));
      await goToDispatchers();
    }
  } catch (error) {
    console.error(error);
  } finally {
    saving.value = false;
  }
}

watch(
  () => props.existedDispatcher,
  (value) => {
    if (value) {
      dispatcher.value = value;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="pa-2 mb-6 pb-10">
    <div class="d-flex align-center justify-space-between">
      <div class="font-weight-medium font-size-16">
        {{ t("general.dispatcher-information") }}
      </div>

      <permission-tooltip
        v-if="existedDispatcher && existedDispatcher.emailConfirmed"
        :permission="DispatcherPermissionValue.ArchiveDispatcher"
      >
        <template #activator="{ disabled }">
          <v-btn
            v-if="!isArchive"
            :disabled="disabled"
            color="primary"
            flat
            prepend-icon="mdi-archive-arrow-down"
            variant="outlined"
            @click="showArchiveDialog = true"
          >
            {{ t("chats.archive") }}
          </v-btn>

          <v-btn
            v-else
            :disabled="disabled"
            color="primary"
            flat
            prepend-icon="mdi-archive-arrow-up"
            variant="outlined"
            @click="showUnArchiveDialog = true"
          >
            {{ t("chats.unarchive") }}
          </v-btn>
        </template>

        {{ t("permission.archive-dispatcher") }}
      </permission-tooltip>

      <v-btn v-else-if="isDelete" color="error" flat prepend-icon="mdi-delete-outline" @click="showRemoveDialog = true">
        {{ t("general.delete") }}
      </v-btn>
    </div>

    <v-form ref="formRef" validate-on="lazy">
      <dispatcher-information v-model="dispatcher" class="mt-3" />

      <dispatcher-permissions v-model="dispatcher.permissions" class="mt-6" />

      <div class="form-buttons">
        <v-btn
          id="cancel-btn"
          class="mr-3 text-primary text-uppercase"
          height="36"
          variant="flat"
          @click="router.back()"
        >
          {{ t("general.cancel") }}
        </v-btn>

        <v-btn
          id="save-btn"
          :loading="saving"
          class="pr-6 pl-6 font-weight-600"
          color="primary"
          height="36"
          variant="flat"
          @click="save"
        >
          {{ t("general.save") }}
        </v-btn>
      </div>
    </v-form>

    <dispatcher-remove-dialog
      v-if="showRemoveDialog"
      v-model="showRemoveDialog"
      :dispatcher="dispatcher"
      @refresh="goToDispatchers"
    />

    <dispatcher-archive-dialog
      v-model:show-archive-dialog="showArchiveDialog"
      v-model:show-un-archive-dialog="showUnArchiveDialog"
      :dispatcher="dispatcher"
      @refresh="goToDispatchers"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.form-buttons {
  position: fixed;
  width: 100%;
  right: 0;
  bottom: 0;
  box-shadow: 0px -8px 20px -7px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  column-gap: 16px;
  background-color: $white;
}
</style>
